import React, { useState, useEffect, useCallback, useRef } from 'react';
import './MobileCardList.css';
import ShareButtons from '../../components/ShareButtons';
import PriceDebugPopup from '../../components/PriceDebugPopup';
import { useTranslation } from 'react-i18next';
import { sealedSelection } from '../../utils/sealedCatalogIdentity';

// Currency symbols for formatting prices
const currencySymbols = {
  USD: '$', EUR: '€', GBP: '£', JPY: '¥', CAD: 'C$', AUD: 'A$', CHF: 'CHF', CNY: '¥', SEK: 'kr', NZD: 'NZ$',
  MXN: 'Mex$', SGD: 'S$', HKD: 'HK$', NOK: 'kr', KRW: '₩', TRY: '₺', RUB: '₽', INR: '₹', BRL: 'R$', ZAR: 'R', AED: 'د.إ'
};

// Preferred condition order: NM first if allowed, then LP, MP, HP, DMG
const EMPTY_CART = [];
const CONDITION_ORDER = ['NM', 'LP', 'MP', 'HP', 'DMG'];

// Games where Japanese is the native/base language (treated like EN for other games)
const JAPANESE_NATIVE_GAMES = ['Pokemon Japan'];

// Helper function to get the native/base language for a game
const getNativeLanguage = (gameName) => {
  if (JAPANESE_NATIVE_GAMES.includes(gameName)) {
    return 'JP';
  }
  return 'EN';
};

// Helper function to get the best default condition based on allowed conditions
const getDefaultCondition = (allowedConditions) => {
  // Find the first condition in preferred order that's allowed
  for (const cond of CONDITION_ORDER) {
    if (allowedConditions.includes(cond)) {
      return cond;
    }
  }
  // Fallback to first available condition
  return allowedConditions[0] || 'NM';
};

// Helper function to sort conditions in preferred order
const sortConditions = (conditions) => {
  return [...conditions].sort((a, b) => {
    const indexA = CONDITION_ORDER.indexOf(a);
    const indexB = CONDITION_ORDER.indexOf(b);
    return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
  });
};

const MobileCardList = ({
  cards,
  addToCart,
  allowedConditions,
  calculatePrice,
  userSettings,
  excludedSku,
  cartItems = EMPTY_CART
}) => {
  const { t } = useTranslation();
  const [expandedCard, setExpandedCard] = useState(null);
  const [selectedConditions, setSelectedConditions] = useState({});
  const [selectedPrintings, setSelectedPrintings] = useState({});
  const [selectedQuantities, setSelectedQuantities] = useState({});
  const [prices, setPrices] = useState({});
  const [showNotBuying, setShowNotBuying] = useState(() => {
    const saved = localStorage.getItem('showNotBuying');
    return saved !== null ? JSON.parse(saved) : true;
  });

  // D-key detection for debug mode
  const isDKeyPressed = useRef(false);
  const [showPriceDebugPopup, setShowPriceDebugPopup] = useState(false);
  const [selectedDebugCard, setSelectedDebugCard] = useState(null);

  // Helper to count matching cart items for inventory-aware pricing
  // Items match when they share: productId + printing + language
  // Note: Different conditions (NM, LP, MP, HP) count TOGETHER toward the threshold
  const countMatchingCartItems = useCallback((productId, printing, language) => {
    if (!cartItems || cartItems.length === 0) return 0;
    return cartItems.filter(item =>
      item.productId === productId &&
      item.printing === printing &&
      item.language === language
    ).length;
  }, [cartItems]);

  // Effect to calculate prices for ALL cards when cards list changes
  // This is needed for the "Show Not Buying" filter to work properly
  useEffect(() => {
    if (!cards || cards.length === 0) return;

    let cancelled = false;
    const calculateAllPrices = async () => {
      const newPrices = {};

      for (const card of cards) {
        const sealed = sealedSelection(card);
        if (sealed.sealed && sealed.error) {
          newPrices[card.productId] = {
            cashPrice: 0, creditPrice: 0, isBulk: false,
            contactForPrice: false, catalogIdentityError: sealed.error
          };
          continue;
        }
        const condition = sealed.sealed
          ? sealed.condition
          : selectedConditions[card.productId] || getDefaultCondition(allowedConditions);
        const uniquePrintings = sealed.sealed
          ? [sealed.printing]
          : [...new Set(card.skus.map(sku => sku.printingName))];
        const printing = sealed.sealed
          ? sealed.printing
          : selectedPrintings[card.productId] || uniquePrintings[0] || 'Normal';

        // Get the native language for this game (JP for Pokemon Japan, EN for others)
        const nativeLanguage = sealed.sealed ? sealed.language : getNativeLanguage(card.gameName);

        // Calculate cart count for inventory-aware pricing
        // This shows what the NEXT unit would cost based on items already in cart
        const cartCount = countMatchingCartItems(card.productId, printing, nativeLanguage);
        const inventoryOffset = cartCount + 1; // Next unit position

        console.log(`[MobileCardList] Cart-aware pricing for ${card.name}:`);
        console.log(`  - productId: ${card.productId}, printing: ${printing}, nativeLanguage: ${nativeLanguage}`);
        console.log(`  - cartItems matching: ${cartCount}, inventoryOffset: ${inventoryOffset}`);

        const priceInfo = await calculatePrice(card, condition, printing, nativeLanguage, true, false, inventoryOffset);
        newPrices[card.productId] = priceInfo;
        console.log(`  - Calculated price: Cash ${priceInfo?.cashPrice}, Credit ${priceInfo?.creditPrice}, Modifier: ${priceInfo?.inventoryModifier || 'none'}`);
      }

      if (!cancelled) setPrices(newPrices);
    };

    calculateAllPrices();
    return () => { cancelled = true; };
  }, [cards, selectedConditions, selectedPrintings, allowedConditions, calculatePrice, cartItems, countMatchingCartItems]); // Include cartItems and selectedPrintings to recalculate when cart or printing changes

  // Effect to recalculate price when expanded card's selections change
  useEffect(() => {
    if (!expandedCard) return;

    const card = cards.find(c => c.productId === expandedCard);
    if (!card) return;

    const sealed = sealedSelection(card);
    if (sealed.sealed && sealed.error) return;
    const condition = sealed.sealed
      ? sealed.condition
      : selectedConditions[card.productId] || getDefaultCondition(allowedConditions);
    const uniquePrintings = sealed.sealed
      ? [sealed.printing]
      : [...new Set(card.skus.map(sku => sku.printingName))];
    const printing = sealed.sealed
      ? sealed.printing
      : selectedPrintings[card.productId] || uniquePrintings[0] || 'Normal';

    let cancelled = false;
    const fetchPrice = async () => {
      // Get the native language for this game (JP for Pokemon Japan, EN for others)
      const nativeLanguage = sealed.sealed ? sealed.language : getNativeLanguage(card.gameName);

      // Calculate cart count for inventory-aware pricing
      // This shows what the NEXT unit would cost based on items already in cart
      const cartCount = countMatchingCartItems(card.productId, printing, nativeLanguage);
      const inventoryOffset = cartCount + 1; // Next unit position

      console.log(`[MobileCardList-expanded] Cart-aware pricing for ${card.name}:`);
      console.log(`  - productId: ${card.productId}, printing: ${printing}, nativeLanguage: ${nativeLanguage}`);
      console.log(`  - cartItems matching: ${cartCount}, inventoryOffset: ${inventoryOffset}`);

      const priceInfo = await calculatePrice(card, condition, printing, nativeLanguage, true, false, inventoryOffset);
      console.log(`  - Calculated price: Cash ${priceInfo?.cashPrice}, Credit ${priceInfo?.creditPrice}, Modifier: ${priceInfo?.inventoryModifier || 'none'}`);

      if (!cancelled) setPrices(prev => ({
        ...prev,
        [card.productId]: priceInfo
      }));
    };

    fetchPrice();
    return () => { cancelled = true; };
  }, [expandedCard, selectedConditions, selectedPrintings, cards, allowedConditions, calculatePrice, cartItems, countMatchingCartItems]);

  // D-key detection for debug mode
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'd' || e.key === 'D') {
        isDKeyPressed.current = true;
      }
    };

    const handleKeyUp = (e) => {
      if (e.key === 'd' || e.key === 'D') {
        isDKeyPressed.current = false;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Handler for D + Click to open Price Debug Popup
  const handleOpenPriceDebug = useCallback((card) => {
    if (isDKeyPressed.current) {
      setSelectedDebugCard(card);
      setShowPriceDebugPopup(true);
      return true;
    }
    return false;
  }, []);

  const closePriceDebugPopup = useCallback(() => {
    setShowPriceDebugPopup(false);
    setSelectedDebugCard(null);
  }, []);

  const toggleCardExpand = (cardId) => {
    if (expandedCard === cardId) {
      setExpandedCard(null);
    } else {
      setExpandedCard(cardId);

      // Initialize condition and quantity if not already set
      if (!selectedConditions[cardId]) {
        setSelectedConditions(prev => ({
          ...prev,
          [cardId]: getDefaultCondition(allowedConditions)
        }));
      }
      
      if (!selectedQuantities[cardId]) {
        setSelectedQuantities(prev => ({
          ...prev,
          [cardId]: 1
        }));
      }
    }
  };

  const handleConditionChange = (cardId, condition) => {
    setSelectedConditions(prev => ({
      ...prev,
      [cardId]: condition
    }));
  };

  const handlePrintingChange = (cardId, printing) => {
    setSelectedPrintings(prev => ({
      ...prev,
      [cardId]: printing
    }));
  };

  const handleQuantityChange = (cardId, quantity) => {
    setSelectedQuantities(prev => ({
      ...prev,
      [cardId]: Math.max(1, Math.min(4, parseInt(quantity) || 1))
    }));
  };

  const handleAddToCart = (card, isCash) => {
    const sealed = sealedSelection(card);
    if (sealed.sealed && sealed.error) {
      window.alert(`${sealed.error} Nothing was added.`);
      return;
    }
    const condition = sealed.sealed
      ? sealed.condition
      : selectedConditions[card.productId] || getDefaultCondition(allowedConditions);
    const quantity = selectedQuantities[card.productId] || 1;
    const uniquePrintings = sealed.sealed
      ? [sealed.printing]
      : [...new Set(card.skus.map(sku => sku.printingName))];
    const printing = sealed.sealed
      ? sealed.printing
      : selectedPrintings[card.productId] || uniquePrintings[0] || 'Normal';
    const skuId = sealed.sealed ? sealed.skuId : card.skus[0]?.skuId || '';

    addToCart(card, condition, printing, quantity, skuId, isCash, sealed.sealed ? sealed.language : 'EN');

    // Close the expanded card after adding to cart
    setExpandedCard(null);
  };

  const formatPrice = (price) => {
    if (!price || price === 0) return '-';
    const currencySymbol = currencySymbols[userSettings?.currency] || '$';
    return `${currencySymbol}${price.toFixed(2)}`;
  };

  const toggleNotBuying = () => {
    const newValue = !showNotBuying;
    setShowNotBuying(newValue);
    localStorage.setItem('showNotBuying', JSON.stringify(newValue));
    
    // Notify parent component that we need more cards if we're hiding not buying items
    if (!newValue && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('needMoreCards', { 
        detail: { showNotBuying: newValue } 
      }));
    }
  };

  if (!cards || cards.length === 0) {
    return (
      <div className="mobile-no-results">
        <p>{t('No cards found. Try a different search.')}</p>
      </div>
    );
  }

  // Filter cards - exclude "code card" items and apply showNotBuying filter
  const filteredCards = cards.filter(card => {
    // Always exclude "code card" items (case-insensitive)
    if (card.name && card.name.toLowerCase().includes('code card')) return false;

    if (showNotBuying) return true;

    // Get price info from state (if available) or use defaults
    const priceInfo = prices[card.productId] || { cashPrice: 0, creditPrice: 0, isBulk: false, isRecentRelease: false };
    const { cashPrice, creditPrice, isBulk, isRecentRelease } = priceInfo;
    const sealed = sealedSelection(card);
    const excludedIdentity = sealed.sealed && !sealed.error ? sealed.skuId : card.skus[0]?.skuId;
    const isExcluded = excludedSku?.includes(excludedIdentity);
    const noPriceAvailable = cashPrice === 0 && creditPrice === 0 && !isBulk;

    // If any of these conditions are true, the card is "not buying"
    const isNotBuying = isRecentRelease || isExcluded || noPriceAvailable || isBulk;

    // Only show the card if it's not a "not buying" card
    return !isNotBuying;
  });

  // Show "no results" message if all cards were filtered out
  if (filteredCards.length === 0) {
    return (
      <div className="mobile-no-results">
        <p>{t('No cards found. Try a different search.')}</p>
      </div>
    );
  }

  return (
    <div className="mobile-card-list">
      <div className="mobile-toggle-container">
        <div className="mobile-view-toggle">
          <label className="mobile-switch">
            <input
              type="checkbox"
              checked={showNotBuying}
              onChange={toggleNotBuying}
            />
            <span className="mobile-slider round"></span>
          </label>
          <span className="mobile-toggle-label">{t('Show Not Buying')}</span>
        </div>
      </div>
      
      {filteredCards.map(card => {
        const sealed = sealedSelection(card);
        const isExpanded = expandedCard === card.productId;
        const sortedConditions = sealed.sealed && !sealed.error
          ? [sealed.condition]
          : sortConditions(allowedConditions);
        const condition = sealed.sealed && !sealed.error
          ? sealed.condition
          : selectedConditions[card.productId] || getDefaultCondition(allowedConditions);
        const quantity = selectedQuantities[card.productId] || 1;
        const uniquePrintings = sealed.sealed && !sealed.error
          ? [sealed.printing]
          : [...new Set(card.skus.map(sku => sku.printingName))];
        const printing = sealed.sealed && !sealed.error
          ? sealed.printing
          : selectedPrintings[card.productId] || uniquePrintings[0] || 'Normal';

        // Get prices from state (async calculated)
        const priceInfo = prices[card.productId] || { cashPrice: 0, creditPrice: 0, isBulk: false, contactForPrice: false, isRecentRelease: false };
        const { cashPrice, creditPrice, isBulk, contactForPrice, isRecentRelease } = priceInfo;
        const noPriceAvailable = !prices[card.productId]; // No price calculated yet
        const notBuying = prices[card.productId] && cashPrice === 0 && creditPrice === 0 && !isBulk && !contactForPrice;

        // Check if SKU is excluded
        const excludedIdentity = sealed.sealed && !sealed.error ? sealed.skuId : card.skus[0]?.skuId;
        const isExcluded = excludedSku?.includes(excludedIdentity);

        return (
          <div
            key={card.productId}
            className={`mobile-card-item ${isExpanded ? 'expanded' : ''} ${isExcluded || isBulk ? 'excluded' : ''}`}
          >
            <div className="mobile-card-header" onClick={() => { if (!handleOpenPriceDebug(card)) toggleCardExpand(card.productId); }}>
              <div className="mobile-card-thumbnail">
                <img
                  src={card.image || process.env.PUBLIC_URL + '/flags/No-Image-Placeholder.svg.png'}
                  alt={card.name}
                  className="mobile-card-thumbnail-img"
                  onError={(e) => {
                    e.target.onerror = null; // Prevent infinite loop
                    e.target.src = process.env.PUBLIC_URL + '/flags/No-Image-Placeholder.svg.png';
                  }}
                />
              </div>
              <div className="mobile-card-info">
                <div className="mobile-card-name">{card.name}</div>
                <div className="mobile-card-set">{card.expansionName}</div>
                {card.number && <div className="mobile-card-number">#{card.number}</div>}
                {!card.number && card.productId && <div className="mobile-card-number">ID: {card.productId}</div>}
                {(isExcluded || isBulk) && (
                  <div className="mobile-card-not-buying">We are not buying</div>
                )}
              </div>
              <div className="mobile-card-expand-icon">
                {isExpanded ? '▲' : '▼'}
              </div>
            </div>
            
            {isExpanded && !isExcluded && !isBulk && (
              <div className="mobile-card-details">
                <div className="mobile-card-selectors">
                  <div className="mobile-card-condition-selector">
                    <label>Condition:</label>
                    <div className="mobile-condition-buttons">
                      {sortedConditions.map(cond => (
                        <button
                          key={cond}
                          className={`mobile-condition-button ${condition === cond ? 'active' : ''}`}
                          onClick={() => handleConditionChange(card.productId, cond)}
                        >
                          {cond}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {uniquePrintings.length > 1 && (
                    <div className="mobile-card-printing-selector">
                      <label>Print Type:</label>
                      <div className="mobile-printing-buttons">
                        {uniquePrintings.map((print, index) => (
                          <button
                            key={index}
                            className={`mobile-printing-button ${printing === print ? 'active' : ''}`}
                            onClick={() => handlePrintingChange(card.productId, print)}
                          >
                            {print}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="mobile-card-quantity">
                  <label>Quantity:</label>
                  <div className="mobile-quantity-control">
                    <button 
                      className="mobile-quantity-button"
                      onClick={() => handleQuantityChange(card.productId, quantity - 1)}
                      disabled={quantity <= 1}
                    >
                      -
                    </button>
                    <span className="mobile-quantity-value">{quantity}</span>
                    <button 
                      className="mobile-quantity-button"
                      onClick={() => handleQuantityChange(card.productId, quantity + 1)}
                      disabled={quantity >= 4}
                    >
                      +
                    </button>
                  </div>
                </div>
                
                <div className="mobile-card-actions">
                  {noPriceAvailable ? (
                    <div className="mobile-loading-price">
                      Loading prices...
                    </div>
                  ) : contactForPrice ? (
                    <div className="mobile-contact-for-price">
                      Contact us for pricing on this item
                    </div>
                  ) : isRecentRelease || notBuying ? (
                    <div className="mobile-not-buying">
                      We are not buying this item
                    </div>
                  ) : (
                    <>
                      {cashPrice > 0 && (
                        <button
                          className="mobile-add-cash"
                          onClick={() => handleAddToCart(card, true)}
                        >
                          Add for Cash ({formatPrice(cashPrice * quantity)})
                        </button>
                      )}

                      {creditPrice > 0 && (
                        <button
                          className="mobile-add-credit"
                          onClick={() => handleAddToCart(card, false)}
                        >
                          Add for Credit ({formatPrice(creditPrice * quantity)})
                        </button>
                      )}

                      {/* Social Share Buttons */}
                      {(cashPrice > 0 || creditPrice > 0) && userSettings.socialMedia !== false && (
                        <ShareButtons
                          cardName={card.name}
                          price={Math.max(cashPrice || 0, creditPrice || 0)}
                          currencySymbol={currencySymbols[userSettings.currency] || '$'}
                          username={userSettings.username}
                          card={card}
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
      {showPriceDebugPopup && selectedDebugCard && (
        <PriceDebugPopup
          card={selectedDebugCard}
          onClose={closePriceDebugPopup}
          calculatePrice={calculatePrice}
          userSettings={userSettings}
        />
      )}
    </div>
  );
};

export default MobileCardList;
